import { CliError, ExitCode } from './errors';

/**
 * Read a value out of a nested object by dotted path (e.g. `job_info.job_title`).
 * Returns undefined if any segment is missing.
 *
 * Uses hasOwnProperty (not the `in` operator / direct index) so a path segment can
 * never traverse into `__proto__` / `constructor` and walk the prototype chain —
 * the input objects are server-controlled, so this is a deliberate safety boundary.
 */
export function getPath(obj: unknown, dottedPath: string): unknown {
	const parts = dottedPath.split('.');
	let cur: unknown = obj;
	for (const p of parts) {
		if (cur && typeof cur === 'object' && Object.prototype.hasOwnProperty.call(cur, p)) {
			cur = (cur as Record<string, unknown>)[p];
		} else {
			return undefined;
		}
	}
	return cur;
}

/**
 * Project an object down to a set of dotted paths, keyed by the path string itself
 * (so `pickPaths(job, ['job_info.job_title'])` → `{ 'job_info.job_title': '...' }`).
 *
 * Every requested path becomes a key so the shape is predictable across a list of
 * heterogeneous items: a missing path yields `null` rather than being dropped, which
 * keeps each row in `jobs search --fields` structurally identical for downstream tools.
 */
export function pickPaths(obj: unknown, paths: string[]): Record<string, unknown> {
	const out: Record<string, unknown> = {};
	for (const p of paths) {
		const v = getPath(obj, p);
		out[p] = v === undefined ? null : v;
	}
	return out;
}

/**
 * Parse a comma-separated `--fields` value into a trimmed, non-empty list.
 * Throws InvalidArgument if the result is empty (e.g. `--fields ,,`).
 */
export function parseFieldsList(raw: string): string[] {
	const fields = raw
		.split(',')
		.map((s) => s.trim())
		.filter(Boolean);
	if (fields.length === 0) {
		throw new CliError('--fields requires at least one field name', ExitCode.InvalidArgument);
	}
	return fields;
}
